/* eslint-disable react/no-unknown-property */
/** @jsxImportSource @emotion/react */
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme } from '@emotion/react';
import Box from '@mui/material/Box';
import MuiButton from '@mui/material/Button';
import Container from '@mui/material/Container';
import Button from '@Button';
import Text from '@Text';
import FetchStatus from '../../Data/FetchStatus';
import { getClientApiLocationsAction } from '../../Hooks/actionCreators';
import {
  goToNextScreen,
  resetSolar,
  setSelectedLocation,
  setSelectedSlot,
  setStepState,
} from '../../Lib/redux/slices/clientApiJourney';
import { updateRecalculateRequiredWidgetHeight } from '../../Lib/redux/slices/dynamicHeight';
import { getPostcodeComponent } from '../__helpers__/component.helpers';
import { selectHasBookableLocations } from '../../Helpers/clientApiLocations.helpers';
import makeStoreListStyles from './makeStoreListStyles';
import {
  getById,
  getByTestId,
  outerHeight,
  useOlbScrollMaxHeight,
  journeyProgressHeight,
} from './clientApiLayout.helpers';
import StoreMap from './StoreMap';
import useClientApiCopy from './copy/useClientApiCopy';

const STORE_VIEWS = {
  LIST: 'list',
  MAP: 'map',
};

const StoreList = ({ componentId, stepKey }) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const classes = makeStoreListStyles({ theme });
  const copy = useClientApiCopy();
  const [storeView, setStoreView] = useState(STORE_VIEWS.LIST);
  const listCardsRef = useRef(null);

  const isDynamicHeightEnabled = useSelector(
    (store) => store.optionalFormConfig?.isDynamicHeightEnabled
  );
  const components = useSelector((store) => store.components);
  const component = useSelector(
    (store) => store.components[`component_${componentId}`]
  );
  const { store_radius: storeRadius, store_radius_unit: storeRadiusUnit } =
    component.component?.additionalSettings ?? {};
  const locationsStatus = useSelector(
    (store) => store.clientApiJourney.location?.listStatus
  );
  const locations = useSelector(
    (store) => store.clientApiJourney.location?.list
  );
  const hasBookableLocations = useSelector(selectHasBookableLocations);
  const selectedLocation = useSelector(
    (store) => store.clientApiJourney.location?.selected
  );

  const postcode = getPostcodeComponent({ components })?.input?.value;

  useEffect(() => {
    if (locationsStatus !== FetchStatus.NOT_STARTED) return;
    dispatch(
      getClientApiLocationsAction({ postcode, storeRadius, storeRadiusUnit })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Same-view changes that don't swap the PageFade view (loading -> list, and
  // the list/map tab toggle) still change the content height, so refresh the
  // widget sizing here. Cross-view transitions are handled by PageFade once the
  // new view has actually mounted.
  useEffect(() => {
    if (isDynamicHeightEnabled) {
      // Dynamic height: recalculate the widget height so the iframe resizes.
      dispatch(
        updateRecalculateRequiredWidgetHeight({
          isWidgetHeightRecalculationRequired: true,
        })
      );
    } else {
      // Static height: nudge the ScrollContainer to re-measure its y-overflow
      // for the new view (it otherwise only recalculates on page transitions).
      window.dispatchEvent(new Event('resize'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationsStatus, selectedLocation, storeView]);

  const handleSelectClick = (location) => {
    dispatch(setSelectedLocation({ selectedLocation: location }));
    dispatch(setSelectedSlot({ selectedSlot: null }));
    dispatch(resetSolar());
    dispatch(setStepState({ stepKey, state: { location } }));
    dispatch(goToNextScreen());
  };

  // Bound the list-view card region to the form's remaining space so the cards
  // scroll while the heading + view tabs stay visible.
  useOlbScrollMaxHeight(
    listCardsRef,
    () => {
      const formContent = getById('formContentContainer');
      if (!formContent) return 0;
      return (
        formContent.clientHeight -
        (formContent.children?.[0]?.clientHeight ?? 0) -
        journeyProgressHeight() -
        outerHeight(getByTestId('storeListTitle')) -
        outerHeight(getByTestId('storeListPostcode')) -
        outerHeight(getByTestId('storeViewToggle')) -
        (getById('footerContainer')?.clientHeight ?? 0)
      );
    },
    [locations, storeView, selectedLocation, isDynamicHeightEnabled],
    !isDynamicHeightEnabled
  );

  // Until there are stores to pick from, keep the loading view: an empty or
  // failed lookup is replaced by ClientApiJourney's failure notice, and the
  // outgoing fade would otherwise show an empty store list before it.
  if (locationsStatus !== FetchStatus.COMPLETED || !hasBookableLocations) {
    return (
      <Container css={classes.messageContainer} data-testid="storeListLoading">
        <Text variant="text">{copy.store.loading}</Text>
      </Container>
    );
  }

  return (
    <Container css={classes.listContainer}>
      <Box css={classes.heading} data-testid="storeListTitle">
        <Text variant="question">{copy.store.heading}</Text>
      </Box>
      <Box css={classes.postcode} data-testid="storeListPostcode">
        <Text variant="text">
          {copy.store.nearYou} {postcode}
        </Text>
      </Box>
      <Box css={classes.viewToggle} data-testid="storeViewToggle">
        <MuiButton
          css={[
            classes.viewToggleTab,
            storeView === STORE_VIEWS.LIST && classes.viewToggleTabActive,
          ]}
          onClick={() => setStoreView(STORE_VIEWS.LIST)}
          data-testid="storeListViewTab"
        >
          {copy.store.listTab}
        </MuiButton>
        <MuiButton
          css={[
            classes.viewToggleTab,
            storeView === STORE_VIEWS.MAP && classes.viewToggleTabActive,
          ]}
          onClick={() => setStoreView(STORE_VIEWS.MAP)}
          data-testid="storeMapViewTab"
        >
          {copy.store.mapTab}
        </MuiButton>
      </Box>
      {storeView === STORE_VIEWS.MAP && (
        <StoreMap locations={locations} onStoreSelect={handleSelectClick} />
      )}
      {storeView === STORE_VIEWS.LIST && (
        <Box css={classes.cardScrollArea} ref={listCardsRef}>
          {locations.map((location) => (
            <Box
              key={location.id}
              css={[
                classes.storeCard,
                location.id === selectedLocation?.id &&
                  classes.storeCardSelected,
              ]}
              data-testid="storeCard"
            >
              <Box css={classes.storeCardHeader}>
                <Box css={classes.storeName}>
                  <Text variant="text">{location.title}</Text>
                </Box>
                <Box css={classes.storeDistance}>
                  <Text variant="text">
                    {location.distance} {location.distanceUnit}
                  </Text>
                </Box>
              </Box>
              <Box css={classes.storeAddress}>
                <Text variant="text">
                  <svg
                    css={classes.addressPin}
                    xmlns="http://www.w3.org/2000/svg"
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0" />
                    <circle cx="12" cy="10" r="3" />
                  </svg>
                  {location.address}
                </Text>
              </Box>
              <Box css={classes.selectButtonContainer}>
                <Button
                  label={copy.store.select}
                  onClick={() => handleSelectClick(location)}
                  dataTestId={`storeSelect-${location.id}`}
                />
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </Container>
  );
};

export default StoreList;
